import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { 
  LuUsers, LuWallet, LuChartBar, LuActivity, LuServer, 
  LuTrendingUp, LuTrendingDown, LuDollarSign, LuCreditCard,
  LuCalendar, LuGlobe, LuShieldCheck
} from 'react-icons/lu';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Компонент карточки статистики
const StatCard = ({ title, value, icon, currency = '', trend = null, trendValue = null }) => {
  return (
    <div className="stat-card" style={{ 
      background: 'var(--card-bg)', 
      borderRadius: '12px', 
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '140px',
      height: '100%'
    }}>
      {/* Верхняя часть с иконкой и трендом */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start', 
        justifyContent: 'space-between', 
        marginBottom: '16px',
        flexShrink: 0
      }}>
        <div style={{ 
          width: '48px', 
          height: '48px', 
          borderRadius: '12px', 
          background: 'var(--accent-color)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: 'white',
          fontSize: '24px',
          flexShrink: 0
        }}>
          {icon}
        </div>
        {trend && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            color: trend === 'up' ? '#22c55e' : '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            flexShrink: 0
          }}>
            {trend === 'up' ? <LuTrendingUp /> : <LuTrendingDown />}
            {trendValue && <span>{trendValue}%</span>}
          </div>
        )}
      </div>
      
      {/* Заголовок - отдельная строка с переносом */}
      <h3 style={{ 
        fontSize: '13px', 
        color: 'var(--text-secondary)', 
        margin: '0 0 12px 0',
        fontWeight: 500,
        lineHeight: '1.4',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        flex: '0 0 auto',
        minHeight: '36px'
      }}>
        {title}
      </h3>
      
      {/* Значение - отдельная строка */}
      <div style={{ 
        fontSize: '28px', 
        fontWeight: 700, 
        color: 'var(--text-primary)',
        lineHeight: '1.2',
        marginTop: 'auto',
        wordBreak: 'break-word',
        overflowWrap: 'break-word'
      }}>
        {currency}{typeof value === 'number' ? value.toLocaleString('ru-RU', { 
          minimumFractionDigits: value % 1 === 0 ? 0 : 2,
          maximumFractionDigits: value % 1 === 0 ? 0 : 2
        }) : value}
      </div>
    </div>
  );
};

// Компонент графика доходов
const RevenueChart = ({ data, period }) => {
  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('ru-RU', {
      day: period === 'days' ? 'numeric' : undefined,
      month: 'short',
      year: period === 'months' ? 'numeric' : undefined
    }),
    USD: item.USD || 0,
    UAH: item.UAH || 0,
    RUB: item.RUB || 0
  }));

  return (
    <div style={{ 
      background: 'var(--card-bg)', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color)',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
        Доходы по {period === 'days' ? 'дням' : period === 'weeks' ? 'неделям' : 'месяцам'}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis 
            dataKey="date" 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              background: 'var(--card-bg)', 
              border: '1px solid var(--border-color)',
              borderRadius: '8px'
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="USD" 
            stroke="#3b82f6" 
            strokeWidth={2}
            name="USD"
            dot={{ r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="UAH" 
            stroke="#22c55e" 
            strokeWidth={2}
            name="UAH"
            dot={{ r: 4 }}
          />
          <Line 
            type="monotone" 
            dataKey="RUB" 
            stroke="#ef4444" 
            strokeWidth={2}
            name="RUB"
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Компонент графика регистраций
const RegistrationsChart = ({ data, period }) => {
  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('ru-RU', {
      day: period === 'days' ? 'numeric' : undefined,
      month: 'short',
      year: period === 'months' ? 'numeric' : undefined
    }),
    count: item.count || 0
  }));

  return (
    <div style={{ 
      background: 'var(--card-bg)', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color)',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
        Регистрации пользователей по {period === 'days' ? 'дням' : period === 'weeks' ? 'неделям' : 'месяцам'}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis 
            dataKey="date" 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              background: 'var(--card-bg)', 
              border: '1px solid var(--border-color)',
              borderRadius: '8px'
            }}
          />
          <Bar dataKey="count" fill="var(--accent-color)" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Компонент графика платежей
const PaymentsChart = ({ data, period }) => {
  const chartData = data.map(item => ({
    date: new Date(item.date).toLocaleDateString('ru-RU', {
      day: period === 'days' ? 'numeric' : undefined,
      month: 'short',
      year: period === 'months' ? 'numeric' : undefined
    }),
    count: item.count || 0
  }));

  return (
    <div style={{ 
      background: 'var(--card-bg)', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color)',
      marginBottom: '24px'
    }}>
      <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
        Количество платежей по {period === 'days' ? 'дням' : period === 'weeks' ? 'неделям' : 'месяцам'}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
          <XAxis 
            dataKey="date" 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <YAxis 
            stroke="var(--text-secondary)"
            style={{ fontSize: '12px' }}
          />
          <Tooltip 
            contentStyle={{ 
              background: 'var(--card-bg)', 
              border: '1px solid var(--border-color)',
              borderRadius: '8px'
            }}
          />
          <Bar dataKey="count" fill="#8b5cf6" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Компонент графика провайдеров платежей
const PaymentProvidersChart = ({ data }) => {
  const COLORS = ['#3b82f6', '#22c55e', '#ef4444', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'];
  
  const chartData = data.map((item, index) => ({
    name: item.provider,
    value: item.count,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <div style={{ 
      background: 'var(--card-bg)', 
      borderRadius: '12px', 
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      border: '1px solid var(--border-color)'
    }}>
      <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
        Распределение платежей по провайдерам
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

function AdminAnalyticsPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('days');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const { token } = useAuth();

  // Функция для быстрого выбора дат
  const setQuickDateRange = (range) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let start, end;
    
    switch(range) {
      case 'today':
        start = new Date(today);
        end = new Date(today);
        break;
      case 'yesterday':
        start = new Date(today);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case 'last7days':
        start = new Date(today);
        start.setDate(start.getDate() - 7);
        end = new Date(today);
        break;
      case 'last30days':
        start = new Date(today);
        start.setDate(start.getDate() - 30);
        end = new Date(today);
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        end = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        return;
    }
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };

  // При изменении периода сбрасываем кастомный диапазон дат
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setDateRange({ start: '', end: '' });
  };

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `/api/admin/analytics?period=${period}`;
        // Если выбран кастомный диапазон дат, используем его, иначе используем период
        if (dateRange.start && dateRange.end) {
          url += `&start_date=${dateRange.start}T00:00:00Z&end_date=${dateRange.end}T23:59:59Z`;
        }
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!response.ok) throw new Error('Не удалось загрузить аналитику');
        const data = await response.json();
        setAnalytics(data);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchAnalytics();
    }
  }, [token, period, dateRange]);

  if (loading) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="loading-mini">Загрузка аналитики...</div>
        </section>
      </main>
    );
  }

  if (error) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="message-error">{error}</div>
        </section>
      </main>
    );
  }

  if (!analytics) {
    return (
      <main className="admin-page-content">
        <section>
          <div className="loading-mini">Нет данных</div>
        </section>
      </main>
    );
  }

  const { overview, revenue_by_period, user_registrations_by_period, payments_by_period, payment_providers, tariffs_analytics, promocodes_analytics, comparison, trial_stats, referral_stats, conversion_funnel, top_users } = analytics || {};

  return (
    <main className="admin-page-content">
      <section>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '32px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2>Аналитика</h2>
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            flexWrap: 'nowrap', 
            alignItems: 'center',
            overflowX: 'auto',
            paddingBottom: '4px'
          }}>
            {/* Быстрый выбор дат - в одну строку */}
            <div style={{ 
              display: 'flex', 
              gap: '4px', 
              alignItems: 'center',
              flexWrap: 'nowrap',
              background: 'var(--card-bg)',
              padding: '6px 10px',
              borderRadius: '10px',
              border: '1px solid var(--border-color)',
              flexShrink: 0
            }}>
              <span style={{ 
                fontSize: '11px', 
                color: 'var(--text-secondary)', 
                marginRight: '6px',
                fontWeight: '500',
                whiteSpace: 'nowrap'
              }}>
                Быстрый выбор:
              </span>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('today')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '60px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                Сегодня
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('yesterday')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '60px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                Вчера
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('last7days')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '65px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                7 дней
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('last30days')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '70px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                30 дней
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('thisMonth')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '85px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                Этот месяц
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDateRange('lastMonth')}
                style={{ 
                  fontSize: '11px', 
                  padding: '5px 10px', 
                  whiteSpace: 'nowrap',
                  minWidth: '115px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                Прошлый месяц
              </button>
            </div>
            
            {/* Кастомный диапазон дат */}
            <div style={{ 
              display: 'flex', 
              gap: '6px', 
              alignItems: 'center',
              flexShrink: 0
            }}>
              <span style={{ 
                fontSize: '11px', 
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap'
              }}>
                Период:
              </span>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                onFocus={(e) => e.target.showPicker?.()}
                style={{
                  padding: '5px 8px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                  minWidth: '110px',
                  maxWidth: '110px'
                }}
                placeholder="Начало"
              />
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>—</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                onFocus={(e) => e.target.showPicker?.()}
                max={new Date().toISOString().split('T')[0]}
                style={{
                  padding: '5px 8px',
                  borderRadius: '6px',
                  border: '1px solid var(--border-color)',
                  background: 'var(--card-bg)',
                  color: 'var(--text-primary)',
                  fontSize: '11px',
                  minWidth: '110px',
                  maxWidth: '110px'
                }}
                placeholder="Конец"
              />
              {(dateRange.start || dateRange.end) && (
                <button
                  className="btn btn-secondary"
                  onClick={() => setDateRange({ start: '', end: '' })}
                  style={{ 
                    minWidth: 'auto', 
                    padding: '5px 10px', 
                    fontSize: '11px',
                    borderRadius: '6px',
                    lineHeight: '1.2'
                  }}
                >
                  Сбросить
                </button>
              )}
            </div>
            
            {/* Кнопки периодов */}
            <div style={{ 
              display: 'flex', 
              gap: '6px', 
              alignItems: 'center',
              flexShrink: 0
            }}>
              <span style={{ 
                fontSize: '11px', 
                color: 'var(--text-secondary)',
                whiteSpace: 'nowrap'
              }}>
                Группировка:
              </span>
              <button
                className={`btn ${period === 'days' && !dateRange.start ? '' : 'btn-secondary'}`}
                onClick={() => handlePeriodChange('days')}
                style={{ 
                  minWidth: '70px',
                  padding: '5px 8px',
                  fontSize: '11px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                <LuCalendar style={{ width: '12px', height: '12px' }} /> Дни
              </button>
              <button
                className={`btn ${period === 'weeks' && !dateRange.start ? '' : 'btn-secondary'}`}
                onClick={() => handlePeriodChange('weeks')}
                style={{ 
                  minWidth: '70px',
                  padding: '5px 8px',
                  fontSize: '11px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                <LuCalendar style={{ width: '12px', height: '12px' }} /> Недели
              </button>
              <button
                className={`btn ${period === 'months' && !dateRange.start ? '' : 'btn-secondary'}`}
                onClick={() => handlePeriodChange('months')}
                style={{ 
                  minWidth: '70px',
                  padding: '5px 8px',
                  fontSize: '11px',
                  borderRadius: '6px',
                  lineHeight: '1.2'
                }}
              >
                <LuCalendar style={{ width: '12px', height: '12px' }} /> Месяцы
              </button>
            </div>
          </div>
        </div>

        {/* Общая статистика */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
            Общая статистика
          </h3>
          <div className="stats-grid" style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            <StatCard 
              title="Всего пользователей" 
              value={overview.total_users} 
              icon={<LuUsers />} 
            />
            <StatCard 
              title="Верифицированных" 
              value={overview.verified_users} 
              icon={<LuShieldCheck />} 
            />
            <StatCard 
              title="Пользователей в боте" 
              value={overview.users_with_telegram} 
              icon={<LuGlobe />} 
            />
            <StatCard 
              title="Всего конфигов" 
              value={overview.total_configs} 
              icon={<LuServer />} 
            />
            <StatCard 
              title="Пользователей с конфигами" 
              value={overview.users_with_configs} 
              icon={<LuActivity />} 
            />
            <StatCard 
              title="Всего платежей" 
              value={overview.total_payments} 
              icon={<LuCreditCard />} 
            />
            <StatCard 
              title="Успешных платежей" 
              value={overview.successful_payments} 
              icon={<LuChartBar />} 
            />
            <StatCard 
              title="Успешность платежей" 
              value={`${overview.payment_success_rate}%`} 
              icon={<LuTrendingUp />} 
            />
            <StatCard 
              title="Доход (USD)" 
              value={overview.total_revenue.USD} 
              currency="$" 
              icon={<LuDollarSign />} 
            />
            <StatCard 
              title="Доход (UAH)" 
              value={overview.total_revenue.UAH} 
              currency="₴" 
              icon={<LuWallet />} 
            />
            <StatCard 
              title="Доход (RUB)" 
              value={overview.total_revenue.RUB} 
              currency="₽" 
              icon={<LuWallet />} 
            />
            {overview.arpu_by_currency && (
              <>
                <StatCard 
                  title="ARPU (USD)" 
                  value={overview.arpu_by_currency.USD} 
                  currency="$" 
                  icon={<LuTrendingUp />} 
                />
                <StatCard 
                  title="ARPU (UAH)" 
                  value={overview.arpu_by_currency.UAH} 
                  currency="₴" 
                  icon={<LuTrendingUp />} 
                />
                <StatCard 
                  title="ARPU (RUB)" 
                  value={overview.arpu_by_currency.RUB} 
                  currency="₽" 
                  icon={<LuTrendingUp />} 
                />
              </>
            )}
            {overview.avg_payment_by_currency && (
              <>
                <StatCard 
                  title="Средний чек (USD)" 
                  value={overview.avg_payment_by_currency.USD} 
                  currency="$" 
                  icon={<LuDollarSign />} 
                />
                <StatCard 
                  title="Средний чек (UAH)" 
                  value={overview.avg_payment_by_currency.UAH} 
                  currency="₴" 
                  icon={<LuDollarSign />} 
                />
                <StatCard 
                  title="Средний чек (RUB)" 
                  value={overview.avg_payment_by_currency.RUB} 
                  currency="₽" 
                  icon={<LuDollarSign />} 
                />
              </>
            )}
            {overview.conversion_rate !== undefined && (
              <StatCard 
                title="Конверсия (регистрации → платежи)" 
                value={`${overview.conversion_rate}%`} 
                icon={<LuChartBar />} 
              />
            )}
            {overview.trial_users !== undefined && (
              <StatCard 
                title="Использовали триал" 
                value={overview.trial_users} 
                icon={<LuActivity />} 
              />
            )}
            {overview.users_with_referrer !== undefined && (
              <StatCard 
                title="Пользователей с рефералом" 
                value={overview.users_with_referrer} 
                icon={<LuUsers />} 
              />
            )}
          </div>
        </div>

        {/* Сравнение периодов */}
        {comparison && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Сравнение периодов
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
                  Новые пользователи
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Сегодня:</span>
                    <strong>{comparison.users.today}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Вчера:</span>
                    <strong>{comparison.users.yesterday}</strong>
                  </div>
                  {comparison.users.this_week > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                        <span>Эта неделя:</span>
                        <strong>{comparison.users.this_week}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Прошлая неделя:</span>
                        <strong>{comparison.users.last_week}</strong>
                      </div>
                      {comparison.users.this_week > comparison.users.last_week && (
                        <div style={{ color: '#22c55e', fontSize: '14px' }}>
                          ↑ Рост на {Math.round(((comparison.users.this_week - comparison.users.last_week) / comparison.users.last_week) * 100)}%
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
                  Платежи
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Сегодня:</span>
                    <strong>{comparison.payments.today}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Вчера:</span>
                    <strong>{comparison.payments.yesterday}</strong>
                  </div>
                  {comparison.payments.this_week > 0 && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
                        <span>Эта неделя:</span>
                        <strong>{comparison.payments.this_week}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Прошлая неделя:</span>
                        <strong>{comparison.payments.last_week}</strong>
                      </div>
                      {comparison.payments.this_week > comparison.payments.last_week && (
                        <div style={{ color: '#22c55e', fontSize: '14px' }}>
                          ↑ Рост на {Math.round(((comparison.payments.this_week - comparison.payments.last_week) / comparison.payments.last_week) * 100)}%
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Графики */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
            Графики
          </h3>
          
          {revenue_by_period && revenue_by_period.length > 0 && (
            <RevenueChart data={revenue_by_period} period={period} />
          )}
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: '24px'
          }}>
            {user_registrations_by_period && user_registrations_by_period.length > 0 && (
              <RegistrationsChart data={user_registrations_by_period} period={period} />
            )}
            
            {payments_by_period && payments_by_period.length > 0 && (
              <PaymentsChart data={payments_by_period} period={period} />
            )}
          </div>
        </div>

        {/* Статистика по тарифам */}
        {tariffs_analytics && tariffs_analytics.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Статистика по тарифам
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
              gap: '24px'
            }}>
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
                  Популярность тарифов
                </h4>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={tariffs_analytics.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis 
                      dataKey="name" 
                      stroke="var(--text-secondary)"
                      style={{ fontSize: '12px' }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis 
                      stroke="var(--text-secondary)"
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        background: 'var(--card-bg)', 
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px'
                      }}
                    />
                    <Bar dataKey="count" fill="var(--accent-color)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
                  Доходы по тарифам
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Тариф</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Продаж</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>USD</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>UAH</th>
                        <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>RUB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tariffs_analytics.map((tariff, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '12px', fontWeight: 500 }}>{tariff.name}</td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>{tariff.count}</td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                            ${(tariff.revenue_by_currency?.USD || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                            ₴{(tariff.revenue_by_currency?.UAH || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                            ₽{(tariff.revenue_by_currency?.RUB || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Воронка конверсии */}
        {conversion_funnel && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Воронка конверсии
            </h3>
            <div style={{ 
              background: 'var(--card-bg)', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { label: 'Регистрации', value: conversion_funnel.registrations, rate: 100, color: '#3b82f6' },
                  { label: 'Верифицированы', value: conversion_funnel.verified, rate: conversion_funnel.rates.verification_rate, color: '#22c55e' },
                  { label: 'В Telegram боте', value: conversion_funnel.with_telegram, rate: conversion_funnel.rates.telegram_rate, color: '#8b5cf6' },
                  { label: 'Использовали триал', value: conversion_funnel.used_trial, rate: conversion_funnel.rates.trial_rate, color: '#f59e0b' },
                  { label: 'Совершили платеж', value: conversion_funnel.made_payment, rate: conversion_funnel.rates.payment_rate, color: '#ef4444' },
                  { label: 'Активная подписка', value: conversion_funnel.active_subscription, rate: conversion_funnel.rates.subscription_rate, color: '#06b6d4' }
                ].map((step, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '8px'
                    }}>
                      <span style={{ fontWeight: 500 }}>{step.label}</span>
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>{step.value}</span>
                        <span style={{ 
                          color: 'var(--text-secondary)', 
                          fontSize: '14px',
                          minWidth: '60px',
                          textAlign: 'right'
                        }}>
                          {step.rate.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '24px',
                      background: 'var(--border-color)',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      position: 'relative'
                    }}>
                      <div style={{
                        width: `${step.rate}%`,
                        height: '100%',
                        background: step.color,
                        borderRadius: '12px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Расширенная статистика по триалам */}
        {trial_stats && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Статистика по триалам
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <StatCard 
                title="Использовали триал" 
                value={trial_stats.total_used} 
                icon={<LuActivity />} 
              />
              <StatCard 
                title="Процент использования триала" 
                value={`${trial_stats.usage_rate}%`} 
                icon={<LuTrendingUp />} 
              />
              <StatCard 
                title="Не использовали триал" 
                value={trial_stats.not_used} 
                icon={<LuUsers />} 
              />
              <StatCard 
                title="Конверсия из триала в платеж" 
                value={`${trial_stats.conversion_from_trial}%`} 
                icon={<LuChartBar />} 
              />
            </div>
          </div>
        )}

        {/* Статистика по реферальной программе */}
        {referral_stats && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Реферальная программа
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '24px'
            }}>
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
                  Общая статистика
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Всего рефералов:</span>
                    <strong>{referral_stats.total_referrals}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Пользователей с рефералами:</span>
                    <strong>{referral_stats.total_referrers}</strong>
                  </div>
                </div>
              </div>
              
              {referral_stats.top_referrers && referral_stats.top_referrers.length > 0 && (
                <div style={{ 
                  background: 'var(--card-bg)', 
                  borderRadius: '12px', 
                  padding: '24px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: '1px solid var(--border-color)'
                }}>
                  <h4 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 600 }}>
                    Топ рефереров
                  </h4>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                          <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>Пользователь</th>
                          <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, fontSize: '14px' }}>Рефералов</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referral_stats.top_referrers.map((ref, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '8px' }}>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontWeight: 500, fontSize: '14px' }}>
                                  {ref.email}
                                </span>
                                {ref.telegram_username && (
                                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                    @{ref.telegram_username}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>
                              {ref.referrals_count}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Топ пользователей по тратам */}
        {top_users && top_users.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Топ пользователей по тратам
            </h3>
            <div style={{ 
              background: 'var(--card-bg)', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>#</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Пользователь</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>USD</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>UAH</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>RUB</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Платежей</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_users.map((user, index) => (
                      <tr key={user.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '12px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                          {index + 1}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontWeight: 500 }}>{user.email}</span>
                            {user.telegram_username && (
                              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                @{user.telegram_username}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>
                          ${(user.spending_by_currency?.USD || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>
                          ₴{(user.spending_by_currency?.UAH || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>
                          ₽{(user.spending_by_currency?.RUB || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>
                          {user.payments_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Статистика по промокодам */}
        {promocodes_analytics && promocodes_analytics.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Статистика по промокодам
            </h3>
            <div style={{ 
              background: 'var(--card-bg)', 
              borderRadius: '12px', 
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Промокод</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Использований</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>USD</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>UAH</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>RUB</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promocodes_analytics.map((promo, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '12px', fontWeight: 500 }}>{promo.code}</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{promo.count}</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                          ${(promo.revenue_by_currency?.USD || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                          ₴{(promo.revenue_by_currency?.UAH || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                          ₽{(promo.revenue_by_currency?.RUB || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Провайдеры платежей */}
        {payment_providers && payment_providers.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 600 }}>
              Провайдеры платежей
            </h3>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
              gap: '24px'
            }}>
              <PaymentProvidersChart data={payment_providers} />
              
              {/* Таблица провайдеров */}
              <div style={{ 
                background: 'var(--card-bg)', 
                borderRadius: '12px', 
                padding: '24px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-color)'
              }}>
                <h3 style={{ margin: '0 0 24px 0', fontSize: '18px', fontWeight: 600 }}>
                  Детали по провайдерам
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'left',
                          fontWeight: 600,
                          color: 'var(--text-primary)'
                        }}>
                          Провайдер
                        </th>
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'right',
                          fontWeight: 600,
                          color: 'var(--text-primary)'
                        }}>
                          Количество
                        </th>
                        <th style={{ 
                          padding: '12px', 
                          textAlign: 'right',
                          fontWeight: 600,
                          color: 'var(--text-primary)'
                        }}>
                          Сумма
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {payment_providers.map((provider, index) => (
                        <tr 
                          key={index}
                          style={{ 
                            borderBottom: '1px solid var(--border-color)',
                            '&:hover': { background: 'var(--hover-bg)' }
                          }}
                        >
                          <td style={{ padding: '12px', fontWeight: 500 }}>
                            {provider.provider}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {provider.count}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                            {provider.total.toLocaleString('ru-RU', { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </main>
  );
}

export default AdminAnalyticsPage;
